import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  Renderer2,
  SimpleChanges,
  EventEmitter,
} from '@angular/core';
import { Params, RouterModule } from '@angular/router';
import { Product } from '@app/akita/api/products/models/product.model';
import { AkitaStartingPricesQuery } from '@app/akita/api/starting-prices/state/starting-prices.query';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import { AkitaScreenQuery } from '@app/akita/screen/state/screen.query';
import { ScreenSizeInfoState } from '@app/akita/screen/state/screen.store';
import { InstallmentsDarkIconComponent } from '@app/shared/components/icons/generated/installments-dark/installments-dark.component';
import { NoSearchIconComponent } from '@app/shared/components/icons/generated/no-search/no-search.component';
import { ReturnDarkIconComponent } from '@app/shared/components/icons/generated/return-dark/return-dark.component';
import { WarrantyDarkIconComponent } from '@app/shared/components/icons/generated/warranty-dark/warranty-dark.component';
import { ApiError } from '@app/shared/models/api/api-error.model';
import { StartingPriceModel } from '@app/shared/models/api/starting-price.model';
import { TranslocoModule } from '@ngneat/transloco';
import { InViewportModule } from 'ng-in-viewport';
import { Observable, Subject, Subscription, tap } from 'rxjs';
import { ProductCardComponent } from '../product-card/product-card.component';
import { HomeNavBarComponent } from '@app/home/components/home-nav-bar/home-nav-bar.component';
import { ProductModelItemComponent } from '@app/phone-brands/components/subcategory-list/product-model-item.component';

@Component({
  selector: 'app-products-collection-grid',
  templateUrl: './products-collection-grid.template.html',
  styleUrls: ['./products-collection-grid.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslocoModule,
    RouterModule,
    InViewportModule,
    ProductCardComponent,
    ProductModelItemComponent,
    HomeNavBarComponent,
    ReturnDarkIconComponent,
    InstallmentsDarkIconComponent,
    WarrantyDarkIconComponent,
    NoSearchIconComponent,
  ],
})
export class ProductsCollectionGridComponent implements OnDestroy, OnChanges {
  @Input() public searchResults?: Array<Product> | null;
  @Input() public loading?: boolean | null;
  @Input() public error?: ApiError | null;

  @Output() public readonly filterPhoneClearAll: EventEmitter<boolean>;

  public biggerThan$: Observable<ScreenSizeInfoState>;
  public phoneSearchFragment$: Observable<Array<string>>;

  public searchParams$: Observable<Params>;
  public searchPhoneModels: Array<Params>;
  public searchPhoneColors: Array<Params>;

  public iPhones$: Observable<Array<StartingPriceModel>>;
  public tablets$: Observable<Array<StartingPriceModel>>;
  public watches$: Observable<Array<StartingPriceModel>>;
  public laptops$: Observable<Array<StartingPriceModel>>;

  public months: string;

  private readonly destroySubject: Subject<void>;
  private readonly subscriptions: Subscription;

  constructor(
    private readonly renderer: Renderer2,
    private readonly akitaScreenQuery: AkitaScreenQuery,
    private readonly akitaRouterQuery: AkitaRouterQuery,
    private readonly akitaStartingPricesQuery: AkitaStartingPricesQuery
  ) {
    this.subscriptions = new Subscription();
    this.destroySubject = new Subject();
    this.filterPhoneClearAll = new EventEmitter<boolean>();

    this.iPhones$ = this.akitaStartingPricesQuery.selectIPhones();
    this.tablets$ = this.akitaStartingPricesQuery.selectTablets();
    this.watches$ = this.akitaStartingPricesQuery.selectWatches();
    this.laptops$ = this.akitaStartingPricesQuery.selectLaptops('Apple');

    this.phoneSearchFragment$ = this.akitaRouterQuery.selectPhoneSearchFragment();

    this.searchPhoneModels = new Array(0);
    this.searchPhoneColors = new Array(0);

    this.searchParams$ = this.akitaRouterQuery.selectQueryParams().pipe(
      tap((params) => {
        if (params.models) {
          this.searchPhoneModels = params.models.split('|').map((model: string) => ({
            models: model,
          }));
        }
        if (params.colors) {
          this.searchPhoneColors = params.colors.split('|').map((color: string) => ({
            colors: color,
          }));
        }
      })
    );

    this.biggerThan$ = this.akitaScreenQuery.selectBiggerThan();
    this.months = '12';
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes) {
      this.searchParams$ = this.akitaRouterQuery.selectQueryParams().pipe(
        tap((params) => {
          if (params.models) {
            this.searchPhoneModels = params.models.split('|').map((model: string) => ({
              models: model,
            }));
          }
          if (params.colors) {
            this.searchPhoneColors = params.colors.split('|').map((color: string) => ({
              colors: color,
            }));
          }
        })
      );
    }

    if (changes.searchResults) {
      this.filterSearchResults();
    }
  }

  /**
   * We do filter the results showing per model
   * the storage variants
   */
  public filterSearchResults(): void {
    if (this.searchResults && this.searchResults?.length) {
      // sort by price ascending
      /* this.searchResults = this.searchResults.sort(
        (a, b) => a.price.amount - b.price.amount
      ); */

      this.searchResults = this.searchResults?.filter(
        (elem, index) =>
          this.searchResults?.findIndex(
            (obj) =>
              obj.model === elem.model &&
              obj.storage === elem.storage &&
              obj.colorCode === elem.colorCode
          ) === index
      );
    }
  }

  public onIntersection({
    target,
    visible,
  }: {
    target: Element;
    visible: boolean;
  }): void {
    this.renderer.setStyle(target, 'visibility', visible ? 'visible' : 'hidden');
  }

  public productFragment(id: number | null): Array<string> {
    return this.akitaRouterQuery.getProductUrlFragment(`${id}`);
  }

  public trackByProduct(index: number, value?: Product | null): string {
    return `${value?.id || index}`;
  }

  public trackByParam(index: number, value?: Params | null): string {
    return `${value?.id || index}`;
  }

  public trackByModel(index: number, category?: any | null): string {
    return (category as any)?.id || category || `${index}`;
  }

  public onClearFilters(): void {
    this.filterPhoneClearAll.emit(true);
  }

  public scrollTop(): void {
    if (
      this.akitaRouterQuery.isBrowser &&
      window.document &&
      window.document.getElementById
    ) {
      const scrollElement = window.document.getElementById('mainAppScroll');
      if (scrollElement) {
        if (scrollElement.scrollTo) {
          scrollElement.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        } else {
          scrollElement.scrollTop = 0;
        }
      }
    }
  }

  public ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();

    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }
}
